import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import { getClientRecord } from '../../api/clientsApi/clientsApi';
import Loader from '../../components/Loader';
import TabsPanel from '../../components/TabsPanel';
import { clientsAccountTabs } from '../../constants/tabRoutes';
import { useHasPermission } from '../../hooks/useHasPermission';
import usePageTitle from '../../hooks/usePageTitle';

const ClientAccountView = () => {
  const { clientId } = useParams();
  const { setPageTitle } = usePageTitle();
  const canViewCases = useHasPermission('Cases', 'View');
  const canViewPlaintiffOnboarding = useHasPermission('Clients', 'Plaintiff Onboarding');
  const showUserOnboardingDetails = localStorage.getItem('showUserOnboardingDetails') === 'true';
  // TODO: types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [entityRecordData, setEntityRecordData] = useState<any>();
  const [isClientRecordLoading, setIsClientRecordLoading] = useState(false);
  const viewPayoutDetails = showUserOnboardingDetails && canViewPlaintiffOnboarding && !!entityRecordData?.isFboPlaintiff;

  const tabs = clientsAccountTabs.filter((tab) => {
    if (tab.path === 'cases') return canViewCases;
    if (tab.path === 'payout-details') return viewPayoutDetails;
    return true;
  });

  const fetchClientRecord = useCallback(() => {
    if (!clientId) {
      setEntityRecordData(undefined);
      return;
    }

    setIsClientRecordLoading(true);
    setEntityRecordData(undefined);

    getClientRecord(clientId)
      .then((res) => {
        if (res.data) {
          setEntityRecordData({
            ...res.data,
            firmId: { id: res.data.firm.id, label: res.data.firm.name },
            address2: res.data.address2 || '',
            middleName: res.data.middleName || '',
            state: res.data.countryState,
            onboardingStatus: res.data.onboardingStatus,
            onboardingConfiguration: res.data.onboardingConfiguration,
          });
        }
      })
      .catch(() => {
        setEntityRecordData(undefined);
      })
      .finally(() => {
        setIsClientRecordLoading(false);
      });
  }, [clientId]);

  useEffect(() => {
    fetchClientRecord();
  }, [fetchClientRecord]);

  // Set here (not in a tab) so the client name stays visible on every tab.
  useEffect(() => {
    if (entityRecordData) {
      setPageTitle && setPageTitle(`${entityRecordData.firstName} ${entityRecordData.lastName}`);
    }
  }, [entityRecordData]);

  useEffect(() => {
    return () => setPageTitle && setPageTitle('');
  }, []);

  return (
    <>
      <Box display="flex" width="100%">
        {entityRecordData && !isClientRecordLoading ? (
          <Box display="flex" flexDirection="column" width="100%" gap="16px">
            <TabsPanel
              tabsList={tabs}
              locationSlicePosition={3}
              contextData={{ ...entityRecordData, refetchClientRecord: fetchClientRecord }}
              page={'Client'}
            />
          </Box>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
            width="100%"
          >
            <Loader colorType="warning" />
          </Box>
        )}
      </Box>
    </>
  );
};

export default ClientAccountView;
